import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SITE_ORIGIN } from '@/config/siteUrl';
import { KAILAC_PHONE_SCHEMA } from '@/config/contactPhone';

interface FAQSchemaItem {
  question: string;
  answer: string;
}

export default function SchemaMarkup() {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language === 'es';
  const baseUrl = SITE_ORIGIN;

  const faqItems: FAQSchemaItem[] = [
    { question: t('faq_1_q'), answer: t('faq_1_a') },
    { question: t('faq_2_q'), answer: t('faq_2_a') },
    { question: t('faq_3_q'), answer: t('faq_3_a') },
    { question: t('faq_4_q'), answer: t('faq_4_a') },
    { question: t('faq_5_q'), answer: t('faq_5_a') },
    { question: t('faq_9_q'), answer: t('faq_9_a') },
    { question: t('faq_10_q'), answer: t('faq_10_a') },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Kailac',
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description: isEs
      ? 'Servicio de asesoría experta para acceder a programas de financiamiento empresarial al 0% de interés. Bilingüe EN/ES.'
      : 'Expert advisory service helping qualified business owners access 0% interest business funding programs. Bilingual EN/ES.',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: KAILAC_PHONE_SCHEMA,
        contactType: 'customer service',
        availableLanguage: ['English', 'Spanish'],
      },
    ],
    sameAs: [
      'https://www.linkedin.com/company/kailac',
      'https://www.facebook.com/kailac',
      'https://www.instagram.com/kailac',
    ],
    foundingDate: '2009',
    areaServed: 'US',
    knowsLanguage: ['en', 'es'],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Kailac',
    url: baseUrl,
    description: isEs
      ? 'Financiamiento empresarial al 0% de interés con guía experta para emprendedores calificados.'
      : '0% interest business funding with expert guidance for qualified entrepreneurs.',
    inLanguage: isEs ? 'es' : 'en',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: 'Kailac',
    url: baseUrl,
    telephone: KAILAC_PHONE_SCHEMA,
    email: 'info@kailac.com',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
    priceRange: '$$',
    description: isEs
      ? 'Asesoría experta para negocios — Financiamiento al 0% de interés por hasta 12 meses para solicitantes calificados.'
      : 'Expert business advisory — 0% interest funding for up to 12 months for qualified applicants.',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '247',
      bestRating: '5',
      worstRating: '1',
    },
  };

  useEffect(() => {
    const schemas = [organizationSchema, websiteSchema, faqSchema, localBusinessSchema];

    const existingScripts = document.querySelectorAll('script[data-schema="kailac-org"]');
    existingScripts.forEach((s) => s.remove());

    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'kailac-org');
      script.setAttribute('data-schema-index', String(i));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="kailac-org"]').forEach((s) => s.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return null;
}
